import { useProviderTypeSearched } from "#/pages/provider-finder/components/results/useProviderTypeSearched"
import { useContentfulEntry } from "#/lib/contentful/hooks/useContentfulEntry"
import { TypeContainerSkeleton } from "#/types/contentful"
import { EntryWithoutUnresolvableLinks } from "#/utils/ContentTypes"
import { defineMessage, MessageDescriptor } from "react-intl"
import { useDoesUserSatisfyConditions } from "#/services/contentful/conditional-gate"
import { toCondition } from "#/lib/contentful/utils/conditionUtils"
import { ProviderType } from "#/pages/provider-finder/utils/parentIdMapping"

export type EnhancedEmptyStateProviderType =
  | ProviderType.Menopause
  | ProviderType.GenderAffirmingHormoneTherapy
  | ProviderType.LowTestosterone

const emptyStateDataByProviderType = {
  [ProviderType.Menopause]: {
    entryId: "5rDP9nftKqh8OwgW5Zy47M",
    displayName: defineMessage({ defaultMessage: "Menopause specialists" })
  },
  [ProviderType.GenderAffirmingHormoneTherapy]: {
    entryId: "18lVZXb70wFLZJqHXlpgLg",
    displayName: defineMessage({ defaultMessage: "Gender-affirming hormone therapy providers" })
  },
  [ProviderType.LowTestosterone]: {
    entryId: "65PMk16AxSXnuaQYGppcIL",
    displayName: defineMessage({ defaultMessage: "Endocrinologists and urologists" })
  }
}

export function useEnhancedEmptyState(): {
  providerType: EnhancedEmptyStateProviderType
  isEnhancedEmptyState: boolean
  emptyStateProviderTypeDisplayName: MessageDescriptor
  emptyStateContent: EntryWithoutUnresolvableLinks<TypeContainerSkeleton>
  isLoadingContent: boolean
} {
  const { providerTypeSearched } = useProviderTypeSearched()
  const doesProviderTypeSupportEnhancedEmptyState =
    Object.keys(emptyStateDataByProviderType).includes(providerTypeSearched)
  const providerType = doesProviderTypeSupportEnhancedEmptyState
    ? (providerTypeSearched as EnhancedEmptyStateProviderType)
    : null

  const { data, isLoading, isError } = useContentfulEntry<TypeContainerSkeleton>(
    doesProviderTypeSupportEnhancedEmptyState ? emptyStateDataByProviderType[providerType].entryId : ""
  )

  const { doesUserSatisfyConditions, isLoading: isDoesUserSatisfyConditionsLoading } = useDoesUserSatisfyConditions()
  const conditions = data?.fields?.conditions?.map(toCondition)
  const doesMeetEnhancedEmptyStateConditions =
    !isDoesUserSatisfyConditionsLoading && doesUserSatisfyConditions(conditions)
  const isEnhancedEmptyState =
    !isError && doesProviderTypeSupportEnhancedEmptyState && doesMeetEnhancedEmptyStateConditions

  return {
    providerType,
    isEnhancedEmptyState,
    emptyStateProviderTypeDisplayName: isEnhancedEmptyState
      ? emptyStateDataByProviderType[providerType].displayName
      : null,
    emptyStateContent: data,
    isLoadingContent: isLoading || isDoesUserSatisfyConditionsLoading
  }
}
